import { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import apiClient from '../apiClient';

/**
 * Custom hook for checking feature toggle status
 * @param {string} featureName - The name of the feature to check
 * @param {number} partnerId - Optional partner ID for partner-specific checks
 * @param {number} tripId - Optional trip ID to resolve partner from trip
 * @param {boolean} disabled - If true, skips the API call and returns true (for testing/override)
 * @returns {Object} - { isEnabled, loading, error, refresh, isAdmin }
 */
export const useFeatureToggle = (featureName, partnerId = null, tripId = null, disabled = false) => {
  const { instance, accounts } = useMsal();
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is admin from token claims (for UI purposes only)
  const isAdmin = accounts[0]?.idTokenClaims?.roles?.includes('Admin') || false;

  const checkFeatureStatus = async () => {
    // Skip API call if explicitly disabled (for testing/override purposes)
    if (disabled) {
      setIsEnabled(true);
      setLoading(false);
      return;
    }

    if (!featureName) {
      setIsEnabled(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const client = await apiClient(instance);
      
      // Build query parameters - prefer partnerId, but can use tripId to resolve partner
      const params = new URLSearchParams();
      if (partnerId) {
        params.append('partnerId', partnerId);
      } else if (tripId) {
        params.append('tripId', tripId);
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await client.get(`/api/Feature/check/${featureName}${queryString}`);
      
      // Backend validates all permissions including admin status
      setIsEnabled(response.data.isEnabled || false);
    } catch (err) {
      console.error(`Error checking feature status for ${featureName}:`, err);
      setError(err.message || 'Failed to check feature status');
      setIsEnabled(false); // Fail closed - deny access on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkFeatureStatus();
  }, [featureName, partnerId, tripId, disabled, instance]);

  return {
    isEnabled,
    loading,
    error,
    refresh: checkFeatureStatus,
    isAdmin // Keep for UI purposes (showing admin indicators, etc.)
  };
};

export default useFeatureToggle;
